import { motion } from 'motion/react';
import { Landmark, Plus, Trash2 } from 'lucide-react';
import { Debt } from '../lib/types';
import { cn, formatCurrency } from '../lib/utils';

interface DebtTabProps {
  debts: Debt[];
  localCurrency: string;
  onManageDebts: () => void;
  onMakePayment: (debt: Debt) => void;
  onDeleteDebt: (id: string) => void;
  isPrivacyMode: boolean;
}

export function DebtTab({ debts, localCurrency, onManageDebts, onMakePayment, onDeleteDebt, isPrivacyMode }: DebtTabProps) {
  const totalInitialDebt = debts.reduce((sum, debt) => sum + debt.initialAmount, 0);
  const totalCurrentBalance = debts.reduce((sum, debt) => sum + debt.currentBalance, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Debts</h2>
        <button onClick={onManageDebts} className="flex items-center gap-2 px-4 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm hover:opacity-90 transition-opacity">
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      <div className="bg-zinc-900 dark:bg-zinc-800 text-white p-8 rounded-[32px] shadow-2xl shadow-zinc-200 dark:shadow-zinc-900/50 transition-colors">
        <div className="text-center space-y-2">
          <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest">Total Debt Remaining</p>
          <h2 className={cn("text-4xl font-bold tracking-tight text-rose-400", isPrivacyMode && "blur-md select-none")}>
            {formatCurrency(totalCurrentBalance, localCurrency)}
          </h2>
          <p className={cn("text-sm text-zinc-400", isPrivacyMode && "blur-sm select-none")}>out of {formatCurrency(totalInitialDebt, localCurrency)}</p>
        </div>
      </div>

      <div className="space-y-4">
        {debts.length === 0 ? (
          <div className="text-center py-12 text-zinc-400 text-sm italic">
            <Landmark className="w-8 h-8 mx-auto mb-2 opacity-50" />
            You have no debts. Add one to start tracking.
          </div>
        ) : (
          debts.map((debt) => {
            const percentage = debt.initialAmount > 0 ? ((debt.initialAmount - debt.currentBalance) / debt.initialAmount) * 100 : 0;
            return (
              <div key={debt.id} className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-zinc-100 dark:border-zinc-800 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-zinc-900 dark:text-zinc-100">{debt.name}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                      <span className={cn("font-bold text-rose-500", isPrivacyMode && "blur-sm select-none")}>{formatCurrency(debt.currentBalance, localCurrency)}</span> remaining
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => onMakePayment(debt)} className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm hover:bg-indigo-700 transition-colors">
                      Pay
                    </button>
                    <button onClick={() => onDeleteDebt(debt.id)} className="p-2 text-zinc-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5 mt-4">
                  <div className={cn("flex justify-between items-center text-[10px] font-medium text-zinc-400", isPrivacyMode && "blur-sm select-none")}>
                    <span className="flex-1">Paid: {formatCurrency(debt.initialAmount - debt.currentBalance, localCurrency)}</span>
                    <span className="flex-1 text-right">Total: {formatCurrency(debt.initialAmount, localCurrency)}</span>
                  </div>
                  <div className="h-2 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className="h-full rounded-full bg-emerald-500"
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}